// components/layout/Header.js
'use client'; // Keep this directive if this component needs client-side rendering
import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image'; // Assuming you use Next.js Image component
import { useRouter } from 'next/router'; // This import is correct
import { useAuth } from '../../hooks/useAuth'; // Assuming named export from useAuth.js
import { useCart } from '../../context/CartContext'; // Assuming named export from CartContext.js
import { useTheme } from '../../context/ThemeContext'; // Assuming named export from ThemeContext.js
import { toast } from 'react-hot-toast'; // Assuming named export from react-hot-toast
import {
  MagnifyingGlassIcon,
  SunIcon,
  MoonIcon,
  ShoppingCartIcon,
  UserCircleIcon,
  ChevronDownIcon,
  Bars3Icon,
  XMarkIcon,
  BellIcon, // Re-added BellIcon for notifications
} from '@heroicons/react/24/outline'; // Named imports for icons
import { useSocket } from '../../context/SocketContext'; // Re-added useSocket if you use it for notifications

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false); // State for notifications dropdown
  const [searchQuery, setSearchQuery] = useState(''); // Search state
  const searchInputRef = useRef(null);
  const mobileSearchInputRef = useRef(null);
  const notificationsRef = useRef(null); // Ref for notifications dropdown

  const router = useRouter();

  const { user, logout, isAuthenticated } = useAuth();
  const { getItemCount } = useCart();
  const { toggleTheme, isDark } = useTheme();
  const { notifications, markAsRead, clearNotification, getUnreadCount, clearAllNotifications } = useSocket();
  const cartItemCount = getItemCount();
  const unreadNotificationsCount = getUnreadCount();

  const handleLogout = async () => {
    await logout();
    toast.success('Déconnexion réussie!');
    router.push('/');
  };

  // Handle search functionality
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to products page with search query
      router.push(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setShowSearchBar(false); // Close mobile search
      setSearchQuery(''); // Clear search after navigation
    }
  };

  const handleSearchInput = (value) => {
    setSearchQuery(value);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch(e);
    }
  };

  // Handle click outside for notification dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target) &&
          !event.target.closest('.notification-toggle')) { // Add a class to the bell icon button for this
        setIsNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isNotificationsOpen]);


  const menuItems = [
    { name: 'Boutique', href: '/products' },
    { name: 'Nouveautés', href: '/nouveautes' },
    { name: 'Notre Histoire', href: '/about' },
    { name: 'Espace Jeux', href: '/jeux' },
    { name: 'Contact', href: '/contact' },
  ];

  useEffect(() => {
    if (showSearchBar && mobileSearchInputRef.current) {
      setTimeout(() => {
        mobileSearchInputRef.current.focus();
      }, 100); // Small delay to ensure the element is rendered
    }
  }, [showSearchBar]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        showUserMenu &&
        !e.target.closest('.user-menu-dropdown') &&
        !e.target.closest('.user-menu-toggle')
      ) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [router.asPath]); // router is now defined here

  return (
    <header className="bg-theme-primary text-theme-primary shadow-sm border-b border-theme-border sticky top-0 z-50 theme-transition backdrop-blur-sm bg-opacity-95">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 min-h-[4rem]">
          {/* Logo + Navigation - Fixed flex-shrink to prevent compression */}
          <div className="flex items-center flex-shrink-0">
            <Link
              href="/"
              className="text-xl font-bold text-theme-primary hover:text-accent transition-colors flex-shrink-0 rainbow-logo"
            >
              Fenkparet
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex ml-6 xl:ml-10 gap-x-3 xl:gap-x-7 items-center">
              {menuItems.map((item) => {
                const isActive = router.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    href={item.href}
                    className={`relative py-2 text-sm font-semibold transition-all duration-300 group shrink-0 text-theme-secondary hover:text-accent ${
                      isActive ? 'brightness-125' : ''
                    }`}
                  >
                    {item.name}
                    {isActive && (
                      <span className="absolute bottom-0 left-0 w-full h-0.5 bg-accent rounded-full animate-fade-in" />
                    )}
                    {!isActive && (
                      <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent rounded-full transition-all duration-300 group-hover:w-full" />
                    )}
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* Right section */}
          <div className="flex items-center gap-1.5 sm:gap-2 lg:gap-3 flex-shrink-0 ml-auto">
            {/* Desktop Search Bar - only at xl to avoid crowding */}
            <form onSubmit={handleSearch} className="hidden xl:flex relative items-center w-52">
              <MagnifyingGlassIcon className="absolute left-3 h-4 w-4 text-gray-400 pointer-events-none" width="16" height="16" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Rechercher..."
                className="pl-9 pr-8 py-1.5 w-full rounded-md border border-theme-border bg-theme-input text-theme-primary placeholder-theme-tertiary focus:outline-none focus-visible:ring-2 focus-visible:ring-accent text-xs transition-all"
              />
              <button type="submit" className="absolute right-2 p-1 text-gray-400 hover:text-accent transition-colors">
                <MagnifyingGlassIcon className="h-3.5 w-3.5" />
              </button>
            </form>

            <button
              onClick={() => setShowSearchBar(!showSearchBar)}
              className="xl:hidden btn-ghost p-3 min-h-[44px] min-w-[44px] flex items-center justify-center group"
              aria-label="Search"
            >
              <MagnifyingGlassIcon className="w-5 h-5 text-theme-secondary group-hover:text-accent transition-colors" width="20" height="20" />
            </button>


            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="group flex items-center gap-2 px-2 py-1.5 sm:px-3 sm:py-2 rounded-lg transition-all duration-300 border border-theme hover:bg-theme-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              aria-label={isDark ? "Passer en mode clair" : "Passer en mode sombre"}
            >
              <div className="relative w-9 h-5 sm:w-10 sm:h-6 bg-theme-tertiary rounded-full p-1 transition-colors duration-300">
                <div 
                  className={`absolute top-0.5 sm:top-1 w-4 h-4 rounded-full transition-all duration-500 transform ${
                    isDark ? 'translate-x-4 bg-accent' : 'translate-x-0 bg-white shadow-sm'
                  } flex items-center justify-center`}
                >
                  {isDark ? (
                    <SunIcon className="w-3 h-3 text-accent-contrast" width="12" height="12" />
                  ) : (
                    <MoonIcon className="w-3 h-3 text-theme-secondary" width="12" height="12" />
                  )}
                </div>
              </div>
              <span className="hidden xl:inline-block text-[10px] sm:text-xs font-bold uppercase tracking-wider text-theme-secondary transition-colors">
                {isDark ? 'Clair' : 'Sombre'}
              </span>
            </button>

            {/* Notifications Icon (if useSocket is active) */}
            {user && ( // Only show notifications if a user is logged in
              (<div className="relative" ref={notificationsRef}>
                <button
                  onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                  className="p-3 rounded-full hover:bg-theme-secondary transition-colors relative notification-toggle min-h-[44px] min-w-[44px] flex items-center justify-center group"
                  aria-label="Notifications"
                >
                  <BellIcon className="w-6 h-6 text-theme-secondary group-hover:text-accent transition-colors" width="24" height="24" />
                  {unreadNotificationsCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-error-color text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center border-2 border-theme-primary">
                      {unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}
                    </span>
                  )}
                </button>
                {isNotificationsOpen && (
                  <div className="absolute right-0 mt-2 w-80 bg-theme-primary rounded-lg shadow-lg border border-theme z-50">
                    <div className="p-4 border-b border-theme flex justify-between items-center">
                      <h3 className="font-semibold text-lg">Notifications</h3>
                      <button onClick={clearAllNotifications} className="text-sm text-theme-tertiary hover:text-accent">
                        Tout effacer
                      </button>
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      {notifications && notifications.length === 0 ? (
                        <p className="text-center text-theme-tertiary p-4">Aucune nouvelle notification.</p>
                      ) : (
                        notifications && notifications.map((notif) => (
                          <div
                            key={notif.id}
                            onClick={() => markAsRead(notif.id)}
                            className={`p-3 border-b border-theme last:border-b-0 cursor-pointer ${
                              notif.read ? 'bg-theme-secondary text-theme-tertiary' : 'bg-theme-bg hover:bg-theme-secondary'
                            }`}
                          >
                            <p className={`font-medium ${notif.read ? 'text-theme-tertiary' : 'text-theme-primary'}`}>
                              {notif.message}
                            </p>
                            <span className="text-xs text-theme-tertiary">
                              {new Date(notif.timestamp).toLocaleString()}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>)
            )}


            {/* Shopping Cart */}
            <Link
              href="/cart"
              className="btn-ghost p-3 relative group min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Shopping cart"
            >
              <ShoppingCartIcon className="w-5 h-5 text-theme-secondary group-hover:text-accent transition-colors" width="20" height="20" />
              {cartItemCount > 0 && (
                <span className="absolute top-1 right-1 bg-accent text-accent-contrast text-[10px] font-black rounded-full h-5 w-5 flex items-center justify-center border-2 border-theme-primary animate-pulse min-w-[1.25rem]">
                  {cartItemCount > 99 ? '99+' : cartItemCount}
                </span>
              )}
            </Link>

            {/* User Auth Menu - Responsive design */}
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => {
                    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
                      setIsMobileMenuOpen(prev => !prev);
                    } else {
                      setShowUserMenu(prev => !prev);
                    }
                  }}
                  className="user-menu-toggle flex items-center space-x-1 sm:space-x-2 btn-ghost px-2 py-2 min-h-[44px] min-w-[44px]"
                  aria-label="User menu"
                >
                  <UserCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-accent flex-shrink-0" width="20" height="20" />
                  <span className="hidden md:block text-sm font-medium max-w-[60px] lg:max-w-[80px] truncate text-theme-primary">
                    {user?.firstName || user?.name || 'Utilisateur'}
                  </span>
                  <ChevronDownIcon
                    className={`w-3 h-3 sm:w-4 sm:h-4 text-theme-secondary transition-transform flex-shrink-0 ${
                      showUserMenu ? 'rotate-180' : ''
                    }`}
                    width="16"
                    height="16"
                  />
                </button>

                {showUserMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                    <div className="user-menu-dropdown absolute right-0 mt-2 w-56 rounded-xl shadow-lg z-50 bg-theme-primary border border-theme animate-fade-in-scale">
                      <div className="py-2">
                        <div className="px-4 py-3 border-b border-theme">
                          <p className="text-sm font-medium text-theme-primary truncate">
                            {user?.firstName || user?.name || 'Utilisateur'}
                          </p>
                          <p className="text-xs text-theme-tertiary truncate">{user?.email}</p>
                        </div>
                        <Link href="/user/profile" className="block px-4 py-2 text-sm nav-link">
                          Profil
                        </Link>
                        <Link href="/user/orders" className="block px-4 py-2 text-sm nav-link">
                          Mes commandes
                        </Link>
                        {user?.isAdmin && (
                          <Link
                            href="/admin/dashboard"
                            className="block px-4 py-2 text-sm nav-link"
                          >
                            Administration
                          </Link>
                        )}
                        <hr className="my-2 border-theme" />
                        <button
                          onClick={() => {
                            logout();
                            setShowUserMenu(false);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm nav-link"
                        >
                          Déconnexion
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <Link 
                href={`/auth/login?redirect=${encodeURIComponent(router.asPath)}`} 
                className="btn-primary text-[10px] xs:text-xs sm:text-sm px-2 py-1.5 sm:px-3 sm:py-2 whitespace-nowrap"
              >
                Connexion
              </Link>
            )}

            {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden btn-ghost p-2 sm:p-3 ml-0.5 sm:ml-1 min-h-[40px] min-w-[40px] sm:min-h-[44px] sm:min-w-[44px] flex items-center justify-center"
                aria-label="Toggle menu"
              >
              {isMobileMenuOpen ? (
                <XMarkIcon className="w-6 h-6 text-theme-secondary" width="24" height="24" />
              ) : (
                <Bars3Icon className="w-6 h-6 text-theme-secondary" width="24" height="24" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Search Bar */}
        {showSearchBar && (
          <div className="lg:hidden p-4 border-t border-theme animate-fade-in-down">
            <form onSubmit={handleSearch} className="relative flex items-center">
              <MagnifyingGlassIcon className="absolute left-3 h-5 w-5 text-theme-secondary pointer-events-none" />
              <input
                ref={mobileSearchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Rechercher..."
                className="pl-10 pr-12 py-2 w-full rounded-md border border-theme-border bg-theme-input text-theme-primary placeholder-theme-tertiary focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus:border-transparent"
                autoFocus
              />
              <button type="submit" className="absolute right-2 p-1 text-theme-secondary hover:text-accent transition-colors">
                <MagnifyingGlassIcon className="h-4 w-4" width="16" height="16" />
              </button>
            </form>
          </div>
        )}

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-theme animate-fade-in-up">
            <div className="py-4 space-y-2">
              {menuItems.map((item) => (
                <Link
                  key={item.name}
                  href={item.href}
                  className="block py-3 px-2 nav-link rounded-md hover:bg-theme-secondary transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}

              {isAuthenticated ? (
                <div className="pt-4 border-t border-theme mt-4 space-y-1">
                  <p className="px-2 pb-1 text-xs font-semibold uppercase tracking-wider text-theme-tertiary">Mon compte</p>
                  <Link href="/user/profile" className="block py-3 px-2 nav-link rounded-md hover:bg-theme-secondary transition-colors" onClick={() => setIsMobileMenuOpen(false)}>
                    Profil
                  </Link>
                  <Link href="/user/orders" className="block py-3 px-2 nav-link rounded-md hover:bg-theme-secondary transition-colors" onClick={() => setIsMobileMenuOpen(false)}>
                    Mes commandes
                  </Link>
                  {user?.isAdmin && (
                    <Link href="/admin/dashboard" className="block py-3 px-2 nav-link rounded-md hover:bg-theme-secondary transition-colors" onClick={() => setIsMobileMenuOpen(false)}>
                      Administration
                    </Link>
                  )}
                  <button
                    className="block w-full text-left py-3 px-2 nav-link rounded-md hover:bg-theme-secondary transition-colors text-red-500"
                    onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
                  >
                    Déconnexion
                  </button>
                </div>
              ) : (
                <div className="pt-4 border-t border-theme mt-4">
                  <Link
                    href={`/auth/login?redirect=${encodeURIComponent(router.asPath)}`}
                    className="block w-full btn-primary text-center"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Connexion
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header; // THIS IS THE ONLY EXPORT FOR THIS FILE
